import React, { useState } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Alert,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import usePageTitle from '@/shared/hooks/usePageTitle';
import MicrosoftSignInButton from './MicrosoftSignInButton';

/**
 * The allowed domains as a phrase to put in a sentence: "@msudenver.edu",
 * "@msudenver.edu or @alumni.msudenver.edu", and so on.
 *
 * @param {string[]} domains
 * @returns {string}
 */
export function allowedDomainsPhrase(domains) {
    const addresses = domains.map((domain) => `@${domain}`);
    if (addresses.length < 2) {
        return addresses.join('');
    }
    if (addresses.length === 2) {
        return `${addresses[0]} or ${addresses[1]}`;
    }
    const last = addresses[addresses.length - 1];
    return `${addresses.slice(0, -1).join(', ')}, or ${last}`;
}

function emailDomain(address) {
    return address.split('@').pop().toLowerCase();
}

function SignUp() {
    usePageTitle('Create an account');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [loading, setLoading] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);

    const { signup, authMethods, passwordAllowedDomains } = useAuth();
    const methods = authMethods;
    const passwordEnabled = methods.includes('password');
    const microsoftEnabled = methods.includes('microsoft');
    // Which addresses this environment lets register, from the server. An
    // empty list admits any; the server refuses anything outside a set list on
    // every request, so creating such an account would strand the person with
    // an account they can never sign in with.
    const domainsPhrase = allowedDomainsPhrase(passwordAllowedDomains);
    const domainHint = domainsPhrase
        ? `Only ${domainsPhrase} addresses can sign up here.`
        : 'Use your MSU Denver email for best results';

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setEmailError('');

        if (
            passwordAllowedDomains.length > 0 &&
            !passwordAllowedDomains.includes(emailDomain(email))
        ) {
            return setEmailError(`Sign up with your ${domainsPhrase} address.`);
        }

        if (password !== confirmPassword) {
            return setError('Passwords do not match');
        }

        if (password.length < 6) {
            return setError('Password must be at least 6 characters');
        }

        try {
            setLoading(true);
            await signup(email, password);
            setVerificationSent(true);
        } catch (err) {
            if (err.code === 'auth/email-already-in-use') {
                setError('An account with this email already exists');
            } else if (err.code === 'auth/invalid-email') {
                setError('Invalid email address');
            } else {
                setError('Failed to create account. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }

    if (verificationSent) {
        return (
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col md={6} lg={5}>
                        <Card>
                            <Card.Body className="p-4 text-center">
                                <h1 className="h2 mb-4">Check Your Email</h1>
                                <Alert variant="success">
                                    A verification link has been sent to{' '}
                                    <strong>{email}</strong>.
                                </Alert>
                                <p className="text-muted">
                                    Please check your email and click the
                                    verification link to activate your account.
                                    Once verified, you can log in and complete
                                    your profile.
                                </p>
                                <Link
                                    to="/login"
                                    className="btn btn-primary mt-3"
                                >
                                    Go to Login
                                </Link>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container className="py-5">
            <Row className="justify-content-center">
                <Col md={6} lg={5}>
                    <Card>
                        <Card.Body className="p-4">
                            <h1 className="h2 text-center mb-2">
                                Create your exchange account
                            </h1>
                            <p className="text-muted text-center mb-4">
                                Use your MSU Denver, CU Denver, CCD, or AHEC
                                email address.
                            </p>
                            {error && <Alert variant="danger">{error}</Alert>}
                            {passwordEnabled && (
                                <Form onSubmit={handleSubmit}>
                                    <Form.Group
                                        className="mb-3"
                                        controlId="email"
                                    >
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            autoComplete="email"
                                            value={email}
                                            onChange={(e) =>
                                                setEmail(e.target.value)
                                            }
                                            required
                                            isInvalid={Boolean(emailError)}
                                            aria-invalid={Boolean(emailError)}
                                            aria-describedby={
                                                emailError
                                                    ? 'signup-email-hint signup-email-error'
                                                    : 'signup-email-hint'
                                            }
                                        />
                                        <Form.Text
                                            id="signup-email-hint"
                                            className="text-muted"
                                        >
                                            {domainHint}
                                        </Form.Text>
                                        {emailError && (
                                            <Form.Control.Feedback
                                                type="invalid"
                                                id="signup-email-error"
                                                role="alert"
                                            >
                                                {emailError}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                    <Form.Group
                                        className="mb-3"
                                        controlId="password"
                                    >
                                        <Form.Label>Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            autoComplete="new-password"
                                            value={password}
                                            onChange={(e) =>
                                                setPassword(e.target.value)
                                            }
                                            required
                                            minLength={6}
                                        />
                                    </Form.Group>
                                    <Form.Group
                                        className="mb-3"
                                        controlId="confirmPassword"
                                    >
                                        <Form.Label>
                                            Confirm Password
                                        </Form.Label>
                                        <Form.Control
                                            type="password"
                                            autoComplete="new-password"
                                            value={confirmPassword}
                                            onChange={(e) =>
                                                setConfirmPassword(
                                                    e.target.value
                                                )
                                            }
                                            required
                                        />
                                    </Form.Group>
                                    <p className="small mb-3">
                                        By creating an account you agree to the{' '}
                                        <Link to="/terms">Terms of use</Link>{' '}
                                        and{' '}
                                        <Link to="/privacy">
                                            Privacy statement
                                        </Link>
                                        .
                                    </p>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="w-100"
                                        disabled={loading}
                                    >
                                        {loading
                                            ? 'Creating account...'
                                            : 'Sign Up'}
                                    </Button>
                                </Form>
                            )}
                            {passwordEnabled && microsoftEnabled && (
                                <div
                                    className="d-flex align-items-center my-3"
                                    aria-hidden="true"
                                >
                                    <hr className="flex-grow-1" />
                                    <span className="px-2 text-muted small">
                                        or
                                    </span>
                                    <hr className="flex-grow-1" />
                                </div>
                            )}
                            {!passwordEnabled && (
                                <p className="text-muted text-center">
                                    Accounts here are created with your MSU
                                    Denver account.
                                </p>
                            )}
                            {microsoftEnabled && (
                                <MicrosoftSignInButton
                                    label="Continue with your MSU Denver account"
                                    redirectTo="/listings"
                                    disabled={loading}
                                    onError={setError}
                                />
                            )}
                        </Card.Body>
                    </Card>
                    <p className="text-center mt-3">
                        Already have an account? <Link to="/login">Log In</Link>
                    </p>
                </Col>
            </Row>
        </Container>
    );
}

export default SignUp;
